import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ArrowLeft, BookOpen, Calendar, ChevronRight, FileText, Home } from "lucide-react";
import PromoSidebar from "../components/PromoSidebar";

const API = window.location.origin;

// Extract year from PYP topic slugs like "previous-year-2023"
function pypYear(slug) {
  const m = slug.match(/previous-year-(\d{4})/);
  return m ? m[1] : null;
}

export default function QuestionBankSubject() {
  const { examType, subject } = useParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/question-bank/public/${examType}/${subject}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [examType, subject]);

  const subjectLabel = data?.subject_label || subject?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const examLabel    = data?.exam_label    || examType?.toUpperCase();

  // Split topics
  const allTopics   = data?.topics || [];
  const pypTopics   = allTopics.filter(t => t.topic_slug?.startsWith("previous-year-") && t.question_count > 0)
                               .sort((a, b) => (pypYear(b.topic_slug) || 0) - (pypYear(a.topic_slug) || 0));
  const regTopics   = allTopics.filter(t => !t.topic_slug?.startsWith("previous-year-"))
                               .sort((a, b) => b.question_count - a.question_count);
  const totalQ      = allTopics.reduce((s, t) => s + t.question_count, 0);

  return (
    <>
      <Helmet>
        <title>{subjectLabel} MCQ for {examLabel} [Free] | EMRS KVS NVS Tier-2 Questions - Descriptive Test</title>
        <meta name="description" content={`${totalQ}+ ${subjectLabel} MCQ questions for ${examLabel} preparation. EMRS, KVS, NVS Tier-2 ${subjectLabel} questions topic-wise with answers and AI-powered explanations. Free on Descriptive Test.`} />
        <meta name="keywords" content={`${subjectLabel} MCQ ${examLabel}, ${subjectLabel} questions for ${examLabel}, EMRS ${examLabel} ${subjectLabel}, KVS ${examLabel} ${subjectLabel}, NVS ${examLabel} ${subjectLabel}, ${subjectLabel} ${examLabel} practice, ${subjectLabel} tier 2 questions, ${subjectLabel} previous year papers`} />
        <meta property="og:title" content={`${subjectLabel} MCQ for ${examLabel} - Descriptive Test`} />
        <meta property="og:description" content={`${totalQ}+ ${subjectLabel} questions for ${examLabel} - EMRS, KVS, NVS Tier-2`} />
        <link rel="canonical" href={`https://descriptivetest.com/question-bank/${examType}/${subject}`} />
      </Helmet>

      <div className="min-h-screen bg-[#0a0d14] text-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0a0d14] via-indigo-950/30 to-[#0a0d14] border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 py-10">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-5">
              <Link to="/dashboard" data-testid="home-btn" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg transition-colors text-slate-300 hover:text-white font-medium">
                <Home className="w-3.5 h-3.5" /> Dashboard
              </Link>
              <span>/</span>
              <Link to="/question-bank" className="hover:text-white transition-colors">Question Bank</Link>
              <span>/</span>
              <Link to={`/question-bank/${examType}`} className="hover:text-white transition-colors">{examType?.toUpperCase()}</Link>
              <span>/</span>
              <span className="text-slate-300">{subjectLabel}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">{subjectLabel} MCQ Questions</h1>
            <p className="text-slate-400 text-sm">
              {examLabel} ·{" "}
              <span className="text-indigo-400 font-bold">{totalQ}</span> total questions ·{" "}
              {regTopics.filter(t => t.question_count > 0).length} topics
              {pypTopics.length > 0 && (
                <span className="ml-2 text-amber-400 font-semibold">· {pypTopics.length} Previous Year Papers</span>
              )}
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8 flex gap-8 items-start">
          <div className="flex-1 min-w-0 space-y-8">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[...Array(8)].map((_, i) => <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />)}
              </div>
            ) : !data ? (
              <div className="text-center py-16 text-slate-400">
                Not found. <Link to="/question-bank" className="text-indigo-400">Go back</Link>
              </div>
            ) : (
              <>
                {/* ── Previous Year Papers Section ─────────────────── */}
                {pypTopics.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar className="w-4 h-4 text-amber-400" />
                      <h2 className="text-base font-bold text-amber-300">Previous Year Papers</h2>
                      <span className="text-xs text-slate-500 ml-1">— click to practice</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {pypTopics.map(topic => {
                        const yr = pypYear(topic.topic_slug);
                        return (
                          <Link
                            key={topic.topic_slug}
                            to={`/question-bank/${examType}/${subject}/${topic.topic_slug}`}
                            data-testid={`pyp-card-${topic.topic_slug}`}
                            className="group flex items-center justify-between bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/25 hover:border-amber-500/50 rounded-xl px-4 py-3.5 transition-all duration-200"
                          >
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/30 border border-amber-500/50 text-amber-300 font-bold uppercase">
                                  PYP
                                </span>
                                {yr && <span className="text-amber-200 font-bold text-sm">{yr}</span>}
                              </div>
                              <p className="text-slate-300 text-xs font-medium">{topic.topic_name}</p>
                              <span className="text-emerald-400 font-bold text-xs">{topic.question_count} Questions</span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-amber-400/60 group-hover:text-amber-300 group-hover:translate-x-1 transition-all shrink-0" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Regular Topic-wise Questions ─────────────────── */}
                {regTopics.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-4 h-4 text-indigo-400" />
                      <h2 className="text-base font-bold text-slate-200">Topic-wise Questions</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {regTopics.map(topic => {
                        const hasQ = topic.question_count > 0;
                        return (
                          <Link
                            key={topic.topic_slug}
                            to={`/question-bank/${examType}/${subject}/${topic.topic_slug}`}
                            data-testid={`topic-card-${topic.topic_slug}`}
                            className={`group flex items-center justify-between bg-white/5 hover:bg-white/10 border border-white/10 hover:border-indigo-500/30 rounded-xl px-5 py-4 transition-all duration-200 ${!hasQ ? "opacity-50 pointer-events-none" : ""}`}
                          >
                            <div>
                              <p className="font-semibold text-sm text-slate-200 group-hover:text-white mb-1">
                                {topic.topic_name}
                              </p>
                              <span className={`text-xs font-bold ${hasQ ? "text-emerald-400" : "text-slate-600"}`}>
                                {topic.question_count} Questions
                              </span>
                            </div>
                            {hasQ ? (
                              <div className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg group-hover:scale-105 transition-transform">
                                Practice →
                              </div>
                            ) : (
                              <span className="text-xs text-slate-600 border border-white/10 px-3 py-1.5 rounded-lg">
                                Coming soon
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {pypTopics.length === 0 && regTopics.filter(t => t.question_count > 0).length === 0 && (
                  <div className="text-center py-16">
                    <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400 mb-1">No questions yet for {subjectLabel}.</p>
                    <p className="text-slate-600 text-sm">Admin can generate questions from LMS Manager → Question Bank.</p>
                  </div>
                )}
              </>
            )}
          </div>
          <PromoSidebar />
        </div>

        <div className="border-t border-white/5 py-6 text-center mt-4">
          <p className="text-slate-600 text-xs">© 2026 <Link to="/" className="text-indigo-500">Descriptive Test</Link></p>
        </div>
      </div>
    </>
  );
}
